import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";

import { supportSubmissionSchema } from "@gately/validators";
import { Button } from "@gately/ui/button";
import { Input } from "@gately/ui/input";
import { Label } from "@gately/ui/label";
import { toast } from "@gately/ui/toast";

import { PublicPageShell } from "~/components/public-page-shell";
import { Turnstile } from "~/components/support/turnstile";
import { orpcClient } from "~/lib/orpc";

export const Route = createFileRoute("/support")({
  component: SupportPage,
});

const SUPPORT_EMAIL = "contact@zenith-ventures.ma";

function SupportPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [token, setToken] = useState("");
  const [resetKey, setResetKey] = useState(0);
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (pending) return;

    const parsed = supportSubmissionSchema.safeParse({
      name,
      email,
      subject,
      message,
      website,
      turnstileToken: token,
    });
    if (!parsed.success) {
      toast.error(
        !token
          ? "Veuillez compléter la vérification anti-robot."
          : "Veuillez vérifier les champs du formulaire.",
      );
      return;
    }

    setPending(true);
    try {
      await orpcClient.support.submit(parsed.data);
      setSent(true);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "L'envoi a échoué. Réessayez plus tard.",
      );
      // Force a fresh Turnstile token — the previous one is single-use.
      setToken("");
      setResetKey((k) => k + 1);
    } finally {
      setPending(false);
    }
  }

  return (
    <PublicPageShell>
      <article className="mx-auto max-w-3xl px-6 py-12">
        <p className="text-primary mb-3 font-mono text-xs tracking-[0.14em] uppercase">
          Gately
        </p>
        <h1 className="text-4xl font-bold tracking-tight">Assistance</h1>
        <p className="text-muted-foreground mt-3 text-base leading-relaxed">
          Une question ou un problème avec l'application Gately ? Écrivez-nous
          via le formulaire ci-dessous, ou directement à{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary font-medium">
            {SUPPORT_EMAIL}
          </a>
          . Nous répondons dès que possible.
        </p>

        {sent ? (
          <div className="border-border mt-10 rounded-xl border p-6">
            <h2 className="text-lg font-semibold">Message envoyé</h2>
            <p className="text-muted-foreground mt-2 text-sm">
              Merci — votre message a bien été transmis à notre équipe. Nous vous
              répondrons par e-mail.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 flex flex-col gap-5">
            <div className="flex flex-col gap-2">
              <Label htmlFor="name">Nom</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                required
                maxLength={100}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                maxLength={254}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="subject">Sujet</Label>
              <Input
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
                maxLength={150}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="message">Message</Label>
              <textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                maxLength={4000}
                rows={6}
                className="border-input placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 dark:bg-input/30 w-full rounded-lg border bg-transparent px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] md:text-sm"
              />
            </div>

            {/* Honeypot — hidden from real users; bots fill it and get dropped. */}
            <div aria-hidden="true" className="hidden">
              <label htmlFor="website">Website</label>
              <input
                id="website"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </div>

            <Turnstile
              onToken={setToken}
              onExpire={() => setToken("")}
              resetKey={resetKey}
            />

            <Button type="submit" disabled={pending || !token} className="w-fit">
              {pending ? "Envoi…" : "Envoyer"}
            </Button>
          </form>
        )}

        <footer className="border-border text-muted-foreground mt-16 border-t pt-6 text-sm">
          <p>
            Consultez aussi nos{" "}
            <Link to="/conditions" className="text-primary font-medium">
              conditions d'utilisation
            </Link>{" "}
            et notre{" "}
            <Link to="/confidentialite" className="text-primary font-medium">
              politique de confidentialité
            </Link>
            .
          </p>
        </footer>
      </article>
    </PublicPageShell>
  );
}
